import { type ClipboardEvent, type CSSProperties, RefObject, useEffect, useRef, useState } from 'react';

interface PasteHintProps {
  open: boolean;
  anchorRect?: DOMRect | null;
  triggerRef?: RefObject<HTMLElement | null>;
  placeholder?: string;
  onPaste: (clipboardText: string) => void;
  onClose: () => void;
}

export default function PasteHint({
  open,
  anchorRect,
  triggerRef,
  placeholder = 'Dan du lieu (Sau khi Ctrl+C tu Excel, Bang du lieu)',
  onPaste,
  onClose
}: PasteHintProps) {
  const popupRef = useRef<HTMLDivElement | null>(null);
  const [text, setText] = useState('');
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        popupRef.current &&
        !popupRef.current.contains(target) &&
        triggerRef?.current &&
        !triggerRef.current.contains(target)
      ) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open, onClose, triggerRef]);

  useEffect(() => {
    if (open) {
      setText('');
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    }
  }, [open]);

  const resolvedAnchorRect = anchorRect || triggerRef?.current?.getBoundingClientRect() || null;

  if (!open || !resolvedAnchorRect) return null;

  const positionStyle: CSSProperties = {
    top: resolvedAnchorRect.bottom + 6,
    left: resolvedAnchorRect.left - 10
  };

  const handlePaste = (event: ClipboardEvent<HTMLTextAreaElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const clipboardText = event.clipboardData?.getData('text') ?? '';
    setText(clipboardText);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      onPaste(clipboardText);
      onClose();
      timeoutRef.current = null;
    }, 1200);
  };

  return (
    <div ref={popupRef} className='fixed z-[9999]' style={positionStyle}>
      <div className='border border-[#4E93DB] bg-white px-2 py-2 shadow-[0_4px_12px_rgba(0,0,0,0.15)] hover:bg-[#b1d1f3]'>
        <div className='border-2 border-black bg-white px-2 py-1'>
          <textarea
            className='h-20 w-48 resize-none border-none bg-transparent text-sm outline-none'
            placeholder={placeholder}
            autoFocus
            value={text}
            onChange={event => setText(event.target.value)}
            onPaste={handlePaste}
            onKeyDown={event => {
              if (event.ctrlKey || event.metaKey) {
                event.stopPropagation();
              }
            }}
          />
        </div>
      </div>
    </div>
  );
}
